<template>
  <div class="signature-pad-container">
    <div ref="signatureCanvas" class="signature-canvas-wrapper">
      <canvas 
        ref="canvas" 
        :width="width" 
        :height="height"
        @mousedown="startDrawing"
        @mousemove="draw"
        @mouseup="stopDrawing"
        @mouseleave="stopDrawing"
        @touchstart="startDrawing"
        @touchmove="draw"
        @touchend="stopDrawing"
        @touchcancel="stopDrawing"
        class="signature-canvas"
        :class="{ 'disabled': disabled }"
      ></canvas>
    </div>
    <div class="signature-controls">
      <b-button size="sm" variant="outline-secondary" @click="clearSignature" :disabled="disabled">
        Clear
      </b-button>
      <b-button size="sm" variant="outline-primary" @click="undoLastPoint" :disabled="disabled || !hasDrawing">
        Undo
      </b-button>
      <b-button size="sm" variant="outline-info" @click="changeColor" :disabled="disabled">
        Change Color
      </b-button>
    </div>
    <div class="mt-2" v-if="!hideInstructions">
      <small class="text-muted">Draw your signature above using mouse or touch</small>
    </div>
  </div>
</template>

<script>
export default {
  name: "SignaturePad",
  props: {
    width: {
      type: Number,
      default: 600
    },
    height: {
      type: Number,
      default: 200
    },
    lineWidth: {
      type: Number,
      default: 2
    },
    disabled: {
      type: Boolean,
      default: false
    },
    hideInstructions: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isDrawing: false,
      lastX: 0,
      lastY: 0,
      ctx: null,
      canvasEl: null,
      color: '#000000',
      history: [],
      colors: ['#000000', '#0000FF', '#FF0000', '#008000', '#800080']
    }
  },
  computed: {
    hasDrawing() {
      return this.history.length > 0;
    }
  },
  mounted() {
    this.initCanvas();
  },
  methods: {
    initCanvas() {
      this.canvasEl = this.$refs.canvas;
      this.ctx = this.canvasEl.getContext('2d');
      
      // Set canvas properties
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
      this.ctx.strokeStyle = this.color;
      this.ctx.lineWidth = this.lineWidth;
      
      // Fill with white background
      this.ctx.fillStyle = '#ffffff';
      this.ctx.fillRect(0, 0, this.width, this.height);
      
      // Add resize observer to handle canvas scaling
      this.resizeCanvas();
      window.addEventListener('resize', this.resizeCanvas);
    },
    
    resizeCanvas() {
      if (!this.canvasEl || !this.ctx) return;
      
      const container = this.$refs.signatureCanvas;
      if (container) {
        const containerWidth = container.clientWidth;
        if (containerWidth < this.width) {
          this.canvasEl.style.width = '100%';
          this.canvasEl.style.height = 'auto';
        }
      }
    },
    
    startDrawing(event) {
      if (this.disabled) return;
      
      event.preventDefault();
      this.isDrawing = true;
      
      const point = this.getCanvasCoordinates(event);
      this.lastX = point.x;
      this.lastY = point.y;
      
      // Start a new path
      this.ctx.beginPath();
      this.ctx.moveTo(this.lastX, this.lastY);
      
      // Save state for undo (but not for every tiny movement)
      if (this.history.length === 0 || this.history.length % 10 === 0) {
        this.saveState();
      }
    },
    
    draw(event) {
      if (!this.isDrawing || this.disabled) return;
      
      event.preventDefault();
      
      const point = this.getCanvasCoordinates(event);
      const currentX = point.x;
      const currentY = point.y;
      
      // Draw line
      this.ctx.lineTo(currentX, currentY);
      this.ctx.stroke();
      
      this.lastX = currentX;
      this.lastY = currentY;
    },
    
    stopDrawing() {
      if (this.isDrawing) {
        this.isDrawing = false;
        this.ctx.beginPath(); // Reset path
        this.saveState(); // Save final state
      }
    },
    
    getCanvasCoordinates(event) {
      const rect = this.canvasEl.getBoundingClientRect();
      const scaleX = this.canvasEl.width / rect.width;
      const scaleY = this.canvasEl.height / rect.height;
      
      let clientX, clientY;
      
      if (event.touches) {
        // Touch event
        clientX = event.touches[0].clientX;
        clientY = event.touches[0].clientY;
      } else {
        // Mouse event
        clientX = event.clientX;
        clientY = event.clientY;
      }
      
      const x = (clientX - rect.left) * scaleX;
      const y = (clientY - rect.top) * scaleY;
      
      // Ensure coordinates are within canvas bounds
      return {
        x: Math.max(0, Math.min(this.canvasEl.width, x)),
        y: Math.max(0, Math.min(this.canvasEl.height, y))
      };
    },
    
    clearSignature() {
      if (this.disabled) return;
      
      this.ctx.clearRect(0, 0, this.width, this.height);
      this.ctx.fillStyle = '#ffffff';
      this.ctx.fillRect(0, 0, this.width, this.height);
      this.ctx.beginPath();
      this.history = [];
      this.$emit('signature-cleared');
    },
    
    saveState() {
      // Save current canvas state for undo
      if (this.history.length > 20) {
        this.history.shift(); // Keep last 20 states max
      }
      this.history.push(this.canvasEl.toDataURL());
    },
    
    undoLastPoint() {
      if (this.disabled || this.history.length <= 1) return;
      
      // Remove current state
      this.history.pop();
      
      // Get previous state
      const lastState = this.history[this.history.length - 1];
      const img = new Image();
      img.src = lastState;
      img.onload = () => {
        this.ctx.clearRect(0, 0, this.width, this.height);
        this.ctx.drawImage(img, 0, 0, this.width, this.height);
      };
    },
    
    changeColor() {
      if (this.disabled) return;
      
      const currentIndex = this.colors.indexOf(this.color);
      const nextIndex = (currentIndex + 1) % this.colors.length;
      this.color = this.colors[nextIndex];
      this.ctx.strokeStyle = this.color;
    },
    
    getSignatureData() {
      // Return the canvas as base64 image
      return this.canvasEl.toDataURL('image/png');
    },
    
    getSignatureBlob(callback) {
      // Return the canvas as blob
      this.canvasEl.toBlob(callback, 'image/png');
    },
    
    isEmpty() {
      // Check if canvas has any non-white/non-transparent pixels
      const pixelData = this.ctx.getImageData(0, 0, this.width, this.height).data;
      for (let i = 0; i < pixelData.length; i += 4) {
        // Check if pixel is not white (allowing some variation)
        if (pixelData[i] < 250 || pixelData[i+1] < 250 || pixelData[i+2] < 250) {
          return false;
        }
      }
      return true;
    },
    
    loadSignatureFromUrl(url) {
      // Load existing signature from URL
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => {
        this.ctx.clearRect(0, 0, this.width, this.height);
        this.ctx.fillStyle = '#ffffff';
        this.ctx.fillRect(0, 0, this.width, this.height);
        this.ctx.drawImage(img, 0, 0, this.width, this.height);
        this.saveState();
      };
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeCanvas);
  }
}
</script>

<style scoped>
.signature-pad-container {
  width: 100%;
  max-width: 100%;
}

.signature-canvas-wrapper {
  border: 2px solid #dee2e6;
  border-radius: 4px;
  background: white;
  margin-bottom: 10px;
  overflow: hidden;
}

.signature-canvas {
  display: block;
  width: 100%;
  height: auto;
  cursor: crosshair;
  touch-action: none; /* Prevents scrolling while drawing on touch devices */
  background: white;
}

.signature-canvas.disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.signature-controls {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
}

@media (max-width: 768px) {
  .signature-controls {
    gap: 5px;
  }
  
  .signature-controls .btn {
    font-size: 12px;
    padding: 0.25rem 0.5rem;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/importables/SignaturePad.vue